import type { Schema, SomeSchema } from './types'
import { completeObjectProperty } from './object-result'

type Executor = SomeSchema['_zod']['run']
interface ExecutionState {
  mode: 'lazy' | 'compiled' | 'interpreted'
  /** Runs before preparation; the first one interprets without preparing. */
  runs: number
  preparations: number
  compilations: number
  reason?: string
  interpreter: Executor
}

// Weak keys: discarded schemas and their generated closures remain collectible.
const states = new WeakMap<SomeSchema, ExecutionState>()

/** Internal diagnostics; intentionally absent from the package barrel. */
export function executionDiagnostics(schema: SomeSchema) {
  const state = states.get(schema)
  return state
    ? { mode: state.mode, runs: state.runs, preparations: state.preparations, compilations: state.compilations, reason: state.reason }
    : undefined
}

/** Internal differential/benchmark harness; no mode selection or global switch. */
export function interpretedExecutor(schema: SomeSchema): Executor {
  const state = states.get(schema)
  if (!state) throw new Error('Schema execution has not been initialized')
  return state.interpreter
}

// Tiering (JIT practice: interpret first, compile on reuse): the first run interprets, so a schema
// built for a single parse never pays eligibility analysis or code generation. The second run prepares.
export function installLazyExecution(schema: SomeSchema) {
  const interpreter = schema._zod.run
  const state: ExecutionState = { mode: 'lazy', runs: 0, preparations: 0, compilations: 0, interpreter }
  states.set(schema, state)
  let executor: Executor | undefined
  schema._zod.run = (payload, ctx) => {
    if (executor) return executor(payload, ctx)
    if (state.runs++ === 0) return interpreter(payload, ctx)
    executor = prepare(schema, state)
    schema._zod.run = executor
    return executor(payload, ctx)
  }
}

function prepare(schema: SomeSchema, state: ExecutionState): Executor {
  const interpreter = state.interpreter
  state.preparations++
  state.mode = 'interpreted'
  const def = schema._zod.def
  if (def.type !== 'object') state.reason = 'category-not-enabled'
  else if (def.checks?.length) state.reason = 'object-checks'
  else if (schema._zod.objectShape) {
    // Do not evaluate recursive shape getters or any user callback to compile.
    const shapeDescriptor = Object.getOwnPropertyDescriptor(def, 'shape')
    const shape: unknown = shapeDescriptor?.value
    if (!shape || typeof shape !== 'object' || Object.values(Object.getOwnPropertyDescriptors(shape)).some((p) => p.get !== undefined || p.set !== undefined)) {
      state.reason = 'shape-accessor'
    } else if (schema._zod.objectIsRecursive?.()) {
      state.reason = 'recursive-schema'
    } else {
      try {
        const compiled = compileObject(schema._zod.objectShape(), interpreter)
        state.mode = 'compiled'
        state.compilations++
        return compiled
      } catch {
        // Includes CSP/new Function refusal; remember the interpreter forever.
        state.reason = 'code-generation-refused'
      }
    }
  } else state.reason = 'unknown-object-runtime'
  return interpreter
}

// Adapted from Zod 4.5.4 generateObjectCheck / compileChild, commit
// e8e206fa33ac5fe7ce20a2beb12d57b1cb3df653. See ../../UPSTREAM.md and LICENSE.zod.
// Unroll keys, hoist children, read each input once, construct parsed output.
// Fork adaptation: runtime children retain full payload/context and failures are
// accumulated in place. Never rerun a child after callbacks have executed.
function compileObject(shape: Record<string, Schema>, interpreter: Executor): Executor {
  const keys = Reflect.ownKeys(shape).filter(key => key !== '__proto__')
  const children = keys.map(key => Reflect.get(shape, key) as Schema)
  const body = keys.map((_key, i) => `{
    const key = keys[${i}];
    const child = children[${i}];
    const value = input[key];
    const parsed = child._zod.run({ value, issues: [] }, ctx);
    if (parsed instanceof Promise) promises.push(parsed.then(p => complete(key, child, p, payload, input, result)));
    else complete(key, child, parsed, payload, input, result);
  }`).join('\n')
  // Keys (including symbols) and child schemas are closed constants, never source text.
  // oxlint-disable-next-line typescript/no-implied-eval -- schema-owned code generation; escaped keys, closed constants, cached CSP fallback
  const factory = new Function('keys', 'children', 'interpreter', 'complete', `
    return function(payload, ctx) {
      const input = payload.value;
      if (typeof input !== 'object' || input === null || Array.isArray(input)) return interpreter(payload, ctx);
      const result = {};
      const promises = [];
      ${body}
      if (promises.length) return Promise.all(promises).then(() => { payload.value = result; return payload; });
      payload.value = result;
      return payload;
    };
  `) as (keys: PropertyKey[], children: Schema[], interpreter: Executor, complete: typeof completeObjectProperty) => Executor
  return factory(keys, children, interpreter, completeObjectProperty)
}
